
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { Download, Upload, RefreshCw, CheckCircle, List, FileImage, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { createTeacherBackup, restoreTeacherBackup, listTeacherBackups } from '@/services/backup/teacherBackup';
import { getTeachers } from '@/services/db/teachers';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import TeacherBackupList from './TeacherBackupList';

const TeacherBackupPanel = () => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [showBackupList, setShowBackupList] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const { data: teachers = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['teachers'],
    queryFn: getTeachers
  });
  
  // Count documents across all teachers
  const documentCount = React.useMemo(() => {
    return teachers.reduce((count, teacher) => {
      return count + (Array.isArray(teacher.documents) ? teacher.documents.length : 0);
    }, 0);
  }, [teachers]);
  
  // Count profile images across all teachers
  const imageCount = React.useMemo(() => {
    return teachers.filter(teacher => Boolean(teacher.profileImage)).length;
  }, [teachers]);
  
  const handleCreateBackup = async () => {
    if (teachers.length === 0) {
      toast.warning('No teacher data to backup');
      return;
    }
    
    setIsCreatingBackup(true);
    setBackupCreated(false);
    
    try {
      await createTeacherBackup();
      setBackupCreated(true);
      setTimeout(() => setBackupCreated(false), 3000);
    } catch (error) {
      console.error('Failed to create teacher backup:', error);
      toast.error('Failed to create teacher backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsRestoringBackup(true);
    
    try {
      const result = await restoreTeacherBackup(file);
      if (result) {
        refetch();
        toast.success('Teacher data restored successfully');
      }
    } catch (error) {
      console.error('Failed to restore teacher backup:', error);
      toast.error('Failed to restore teacher backup');
    } finally {
      setIsRestoringBackup(false);
      // Clear the input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const toggleBackupList = () => {
    setShowBackupList(!showBackupList);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          Teacher Backup System
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          {isLoading ? 'Loading...' : isError ? 'Error loading teachers' : 
            `${teachers.length} teachers available for backup`}
        </div>
        
        {!isLoading && !isError && (
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 mb-4 text-xs text-muted-foreground">
            <div className="flex items-center">
              <FileImage className="h-3 w-3 mr-1 text-blue-500" />
              <span>{imageCount} profile images</span>
            </div>
            <div className="flex items-center">
              <FileText className="h-3 w-3 mr-1 text-orange-500" />
              <span>{documentCount} documents</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleCreateBackup}
              disabled={isCreatingBackup || teachers.length === 0}
              variant={backupCreated ? "secondary" : "default"}
            >
              {isCreatingBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Creating Backup...</span>
                </>
              ) : backupCreated ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span>Backup Created!</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  <span>Create Teacher Backup</span>
                </>
              )}
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Button
              className="w-full h-14 flex items-center justify-center space-x-2"
              onClick={handleSelectFile}
              disabled={isRestoringBackup}
              variant="outline"
            >
              {isRestoringBackup ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  <span>Restoring Backup...</span>
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  <span>Restore from File</span>
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json"
                className="hidden"
              />
            </Button>
          </motion.div>
        </div>
        
        <Button
          variant="ghost"
          className="w-full mt-2"
          onClick={toggleBackupList}
        >
          <List className="h-4 w-4 mr-2" />
          {showBackupList ? "Hide Backup History" : "View Backup History"}
        </Button>
        
        {showBackupList && (
          <TeacherBackupList onRestoreComplete={() => refetch()} />
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherBackupPanel;
